import { startOfDay } from '@/helpers/date';
import { isServiceBookable } from '@/helpers/placeHelper';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { Place, PlaceEmployee, PlaceService, PlaceServiceGroup } from '@/types/state/shared';
import { SelectServiceCategory, SelectServiceItem } from './SelectService.types';

/**
 * Drop services the customer has already picked, so the picker only offers what's
 * left to add. This and the two helpers below (onlyServicesPerformedBy,
 * withoutEmptyCategories) intentionally duplicate BookAppointment's
 * filterBookPlaceServicesByPickedServices / BySelectedEmployee / ByEmptyCategories
 * rather than importing them, so booking-selection stays self-contained and
 * widget-safe as it replaces that legacy flow. Keep the copies in sync by
 * hand until the legacy view is removed.
 */
function withoutPickedServices(
  serviceCategories: PlaceServiceGroup[],
  pickedServices: PlaceService[],
): PlaceServiceGroup[] {
  return serviceCategories.map((category) => ({
    ...category,
    services: (category?.services || []).filter((service) =>
      pickedServices.every((pickedService) => pickedService.id !== service.id),
    ),
  }));
}

function onlyServicesPerformedBy(
  serviceCategories: PlaceServiceGroup[],
  pickedEmployee: PlaceEmployee,
): PlaceServiceGroup[] {
  return serviceCategories.reduce<PlaceServiceGroup[]>((acc, category) => {
    const performed = category.services.filter((service) => pickedEmployee.services?.includes(service.id));
    if (performed.length) acc.push({ ...category, services: performed });
    return acc;
  }, []);
}

function withoutEmptyCategories(serviceCategories: PlaceServiceGroup[]): PlaceServiceGroup[] {
  return serviceCategories.filter((category) => category.services?.length);
}

/** Id of the synthetic "Kampanjer" category; real category ids are positive database ids. */
export const CAMPAIGNS_CATEGORY_ID = -1;

const DISCOUNT_CAMPAIGN_TYPE = 6;

/**
 * Ids of the services covered by a discount campaign running today. Mirrors the place
 * page's Categories (only type 6 shows as a group; other campaign types still price the
 * row through getServiceCampaignIfAny but get no section of their own).
 */
function getActiveCampaignServiceIds(place: Place | undefined, now: number = startOfDay()): Set<number> {
  const ids = new Set<number>();
  for (const campaign of place?.campaigns ?? []) {
    if (campaign?.type !== DISCOUNT_CAMPAIGN_TYPE) continue;
    if (campaign.startDate == null || campaign.endDate == null) continue;
    if (now < campaign.startDate * 1000 || now > campaign.endDate * 1000) continue;
    for (const service of campaign.services ?? []) ids.add(service.id);
  }
  return ids;
}

/**
 * The picker's leading "Kampanjer" section: every service across the already-narrowed
 * categories that an active discount campaign covers, deduplicated. Built from the
 * narrowed pool (not from place.campaigns directly) so the picked-services / employee /
 * includeService rules apply to it the same way as to every other category.
 */
export function getCampaignCategory(
  place: Place | undefined,
  categories: PlaceServiceGroup[],
  now?: number,
): PlaceServiceGroup | null {
  const campaignIds = getActiveCampaignServiceIds(place, now);
  if (campaignIds.size === 0) return null;

  const seen = new Set<number>();
  const services = categories
    .flatMap((category) => category.services)
    .filter((service) => {
      if (!campaignIds.has(service.id) || seen.has(service.id)) return false;
      seen.add(service.id);
      return true;
    });
  if (services.length === 0) return null;

  return { id: CAMPAIGNS_CATEGORY_ID, name: '', own: true, services };
}

/**
 * The set of categories (and bookable services within them) the picker should show,
 * applying the same filters the legacy pick-service view used: hide already-picked
 * services, restrict to the selected employee, keep only bookable services, and drop
 * categories that end up empty. The bookable filter runs here (not at render time, as
 * before) so the per-category service count shown in the redesigned header is accurate.
 */
export function getBookableCategories(
  place: Place,
  pickedServices: PlaceService[],
  pickedEmployee?: PlaceEmployee,
): PlaceServiceGroup[] {
  let categories = withoutEmptyCategories(withoutPickedServices(place?.services || [], pickedServices ?? []));

  if (pickedEmployee) {
    categories = onlyServicesPerformedBy(categories, pickedEmployee);
  }

  categories = categories.map((category) => ({
    ...category,
    services: category.services.filter((service) => isServiceBookable(service, place)),
  }));

  return withoutEmptyCategories(categories);
}

/**
 * The service ids some single employee can perform alongside the whole current selection.
 *
 * Extra-time add-ons are dropped from what an employee is required to perform: they live on
 * `service.extra.addOnServices`, never in `employee.services`, so requiring one would exclude
 * every employee rather than the ones who cannot do it. Their parent service carries the
 * capability. Employees who are not bookable, are inactive, or perform nothing at all cannot
 * staff the selection either.
 */
function servicesPerformableWith(pickedServices: PlaceService[], employees: PlaceEmployee[]): Set<number> {
  const requiredIds = pickedServices
    .filter((service) => !serviceIsExtraTimeAddOn(service))
    .map((service) => service.id);

  const performable = new Set<number>();
  employees.forEach((employee) => {
    if (!employee.about?.isBookable || employee.about.inactive) return;

    const performs = employee.services ?? [];
    if (requiredIds.every((id) => performs.includes(id))) performs.forEach((id) => performable.add(id));
  });

  return performable;
}

/**
 * The categories the calendar's "Lägg till fler tjänster" modal may offer: the bookable
 * pool restricted to services someone who performs the whole current selection can also
 * perform, with add-ons dropped (they are the other link's job) and empty categories removed.
 *
 * With a concrete employee picked, getBookableCategories has already narrowed the pool to
 * that employee. With "Valfri utövare" it has narrowed nothing, so a service only stays if
 * some employee performs it *together with* everything already picked — otherwise the pick
 * would leave the booking unstaffable.
 */
export function getAddableCategories(
  place: Place,
  pickedServices: PlaceService[],
  pickedEmployee?: PlaceEmployee,
): PlaceServiceGroup[] {
  const categories = getBookableCategories(place, pickedServices, pickedEmployee);
  const performable = pickedEmployee
    ? undefined
    : servicesPerformableWith(pickedServices ?? [], place?.employees ?? []);

  return withoutEmptyCategories(
    categories.map((category) => ({
      ...category,
      services: category.services.filter(
        (service) => !service?.about?.settings?.isAddOn && (!performable || performable.has(service.id)),
      ),
    })),
  );
}

/** Id of the synthetic "Matchande tjänster" section shown while searching. */
export const MATCHED_SERVICES_CATEGORY_ID = -2;

/**
 * Client-side (not server-backed) service search. Flattens every category into one list
 * (mirroring the place page's single "Matchande tjänster" section) and dedupes by id, since
 * a campaign service is present both in "Kampanjer" and in its real category. Category
 * order is kept, so a campaign service surfaces in its Kampanjer position.
 */
export function matchServicesByQuery(categories: SelectServiceCategory[], query: string): SelectServiceItem[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return [];

  const seen = new Set<number>();
  return categories
    .flatMap((category) => category.services)
    .filter((service) => {
      if (seen.has(service.id) || !service.name.toLowerCase().includes(normalized)) return false;
      seen.add(service.id);
      return true;
    });
}
